import { api } from './client';

export interface RondoUserListItem {
  id: number;
  name: string;
  nickname: string | null;
  email: string | null;
  mobile: string | null;
  country: string;
  currency: string;
  balance: number;
  credits: number;
  cashBalance: number;
  readyToPayoff: number;
  tip30d: number;
  tipTotal: number;
  spin30d: number;
  leads: number;
  shScore: number | null;
  segment: string | null;
  segments: Array<{ name: string; color: string }>;
  status: 'active' | 'inactive' | 'blocked';
  isActive90d: boolean;
  lastActiveAt: string | null;
}

export interface RondoUserListResult {
  items: RondoUserListItem[];
  total: number;
}

export interface RondoUserListParams {
  search?: string;
  country?: string;
  status?: string;
  segment?: string;
  activeDays?: number;
  inactive90?: boolean;
  limit?: number;
  offset?: number;
}

export interface RondoCountryStat {
  country: string;
  currency: string;
  total: number;
  active1d: number;
  active7d: number;
  active30d: number;
  active90d: number;
  registrace30d: number;
  inactiveTotal: number;
  spinUsers: number;
  walletIn: number;
  walletOut: number;
}

export interface RondoUserStats {
  total: number;
  // Technický stav účtu v mirroru. Pro počty aktivních používej active.d90.
  activeStatus: number;
  active: { d1: number; d7: number; d30: number; d90: number };
  activeLast90Logins: number;
  inactiveTotal: number;
  byCountry: RondoCountryStat[];
  audit: { rondoOverview90d: number; ourDb90d: number; diff: number };
}

export function getRondoUserStats(): Promise<RondoUserStats> {
  return api.get<RondoUserStats>('/api/v1/rondo/users/stats');
}

export type PayoffStatus = 'new' | 'pending' | 'sent' | 'delivered' | 'rejected';
export interface PayoffBucket {
  count: number;
  byCurrency: Record<string, number>;      // SUM(amount) per měna
  countByCurrency: Record<string, number>; // COUNT per měna
  oldest: string | null;
  latest: string | null;
}
export interface PayoffTodayBatch extends PayoffBucket {
  byCountry: Record<string, number>;
}
export interface PayoffCountryRow {
  new: number;
  pending: number;
  held: number;
  delivered: number;
  failed: number;
}
export interface PayoffRequests {
  count: number;
  byCountry: Record<string, number>;
  truncated: boolean;
  fetchedAt: string;
}
export interface RondoPayoffStats {
  todayBatch: PayoffTodayBatch;
  new: PayoffBucket;
  pending: PayoffBucket;
  held: PayoffBucket;
  delivered: PayoffBucket & { windowDays: number };
  failed: PayoffBucket;
  byCountry: Record<string, PayoffCountryRow>;
  totalCount: number;
  requests: PayoffRequests | null;
  syncedAt: string | null;
  dbTotal: number;
}

export function getRondoPayoffStats(): Promise<RondoPayoffStats> {
  return api.get<RondoPayoffStats>('/api/v1/rondo/payoffs/stats');
}

export interface RondoPayoffVyuctovaniRow {
  period: string;
  currency: string;
  count: number;
  volume: number;
}
export function getRondoPayoffsVyuctovani(params: {
  granularity: 'day' | 'month';
  from?: string;
  to?: string;
}): Promise<RondoPayoffVyuctovaniRow[]> {
  const p = new URLSearchParams();
  p.set('granularity', params.granularity);
  if (params.from) p.set('from', params.from);
  if (params.to) p.set('to', params.to);
  return api.get<RondoPayoffVyuctovaniRow[]>(`/api/v1/rondo/payoffs/vyuctovani?${p.toString()}`);
}

export interface RondoSpinItem {
  date: string;
  spinHistoryId: number;
  game: string;
  item: string | null;
  winDate: string | null;
  orderId: number | null;
  tags: string[];
}
export interface RondoCreditLogItem {
  date: string;
  type: 'add' | 'subtract' | 'transfer' | 'bonus';
  amount: number;
  balanceAfter: number;
  note: string;
}
export interface RondoWalletLogItem {
  date: string;
  type: 'in' | 'out';
  amount: number;
  currency: string;
  balanceAfter: number;
  note: string;
}
export interface RondoOrderItem {
  orderId: number;
  createdAt: string;
  status: string;
  product: string;
  price: number;
  currency: string;
}
export interface RondoTipovackaBet {
  date: string;
  game: string;
  tip: string;
  correct: boolean | null;
  amount: number;
  currency: string;
}
export interface RondoTipovackaWinner {
  date: string;
  game: string;
  detail: string;
}
export interface RondoTipovackaReward {
  date: string;
  game: string;
  type: string;
  amount: number;
  currency: string;
}
export interface RondoTipovackaProof {
  date: string;
  game: string;
  detail: string;
}
export interface RondoTipovacka {
  summary: { bets: number; won: number; rewards: number };
  bets: RondoTipovackaBet[];
  winners: RondoTipovackaWinner[];
  rewards: RondoTipovackaReward[];
  proofs: RondoTipovackaProof[];
}
export interface RondoBellWin {
  date: string;
  detail: string;
  amount: number;
  currency: string;
}
export interface RondoBehavioral {
  totalEvents: number;
  bestSendTime: number;
  topEvents: { name: string; count: number }[];
  byHour: number[];
  partnerAffinity: { partner: string; score: number }[];
  superhrac: { score: number; dims: { name: string; points: number }[] };
  weather: { condition: string; engagement: number }[];
}
export interface RondoPartnerLead {
  partner: string;
  points: number;
  status: 'nic' | 'otevřeno' | 'dokončeno';
  sharedVar: string;
  generatedUrl: string;
}
export interface RondoUdaje {
  gender: string;
  acquisitionSource: string;
  mailkitStatus: string;
  mailTesting: boolean;
  superhrac: boolean;
}

export interface RondoUserDetail {
  id: number;
  name: string;
  firstNameVocative: string | null;
  nickname: string | null;
  email: string | null;
  mobile: string | null;
  country: string;
  currency: string;
  status: 'active' | 'inactive' | 'blocked';
  isActive90d: boolean;
  segment: string | null;
  balance: number;
  credits: number;
  rondonyVault: number;
  cashBalance: number;
  readyToPayoff: number;
  registeredAt: string | null;
  lastActiveAt: string | null;
  lastBetAt: string | null;
  tip30d: number;
  tipTotal: number;
  spin30d: number;
  leads: number;
  shScore: number | null;
  loyaltyTier: string | null;
  loyaltyVipPoints: number | null;
  spins: RondoSpinItem[];
  creditLog: RondoCreditLogItem[];
  walletLog: RondoWalletLogItem[];
  orders: RondoOrderItem[];
  tipovacka: RondoTipovacka;
  bellWins: RondoBellWin[];
  behavioral: RondoBehavioral;
  partnerLeads: RondoPartnerLead[];
  udaje: RondoUdaje;
}

export interface RondoUserDetailResponse {
  user: RondoUserDetail;
  payoffs: RondoPayoffItem[];
}

export function getRondoUserDetail(id: number): Promise<RondoUserDetailResponse> {
  return api.get<RondoUserDetailResponse>(`/api/v1/rondo/users/${id}`);
}

// Per-user marketing engagement (Marketing tab) - email otevření/kliky, modal,
// notifikace. Data z p1_space_rondo (klíč rondo_user_id).
export interface RondoUserMarketing {
  email: {
    campaigns: number; opened: number; clicked: number;
    opensTotal: number; clicksTotal: number;
    openRate: number | null; clickRate: number | null;
    lastSentAt: string | null; lastOpenAt: string | null; lastClickAt: string | null;
  };
  modal: { shown: number; clicked: number; dismissed: number; lastAt: string | null };
  notif: { delivered: number; shown: number; clicked: number; lastAt: string | null };
  recent: Array<{ id: number; name: string; channel: string; state: string; sentAt: string | null; openCount: number; clickCount: number }>;
}

export function getRondoUserMarketing(id: number): Promise<RondoUserMarketing> {
  return api.get<RondoUserMarketing>(`/api/v1/rondo/users/${id}/marketing`);
}

// Toggle segmentových flagů uživatele (Mail testing / Superhráč).
export function setRondoUserFlags(
  id: number,
  flags: { testMail?: boolean; superHrac?: boolean },
): Promise<{ updated: boolean }> {
  return api.patch<{ updated: boolean }>(`/api/v1/rondo/users/${id}/flags`, flags);
}

export interface RondoSegmentRule { metric: string; op: string; value: string | number }
export interface RondoSegmentItem {
  id: number;
  name: string;
  color: string;
  country: string;
  userCount: number | null;
  ruleCount: number;
  ruleSummary: string | null;
  rules: RondoSegmentRule[];
  priority: number;
  isActive: boolean;
  inList: boolean;
  partner: string | null;
}

export interface RondoSegmentInput {
  name: string; country: string; color: string; priority: number;
  active: boolean; inList: boolean;
  rules: Array<{ metric: string; op: string; value: string | number }>;
}
export function createRondoSegment(input: RondoSegmentInput): Promise<{ id: number }> {
  return api.post<{ id: number }>('/api/v1/rondo/segments', input);
}
export function updateRondoSegment(id: number, input: RondoSegmentInput): Promise<{ updated: boolean }> {
  return api.put<{ updated: boolean }>(`/api/v1/rondo/segments/${id}`, input);
}
export function deleteRondoSegment(id: number): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(`/api/v1/rondo/segments/${id}`);
}

export interface RondoSegmentListResult {
  items: RondoSegmentItem[];
  total: number;
}

export function listRondoSegments(params: {
  search?: string;
  country?: string;
  partner?: string;
  stav?: string;
} = {}): Promise<RondoSegmentListResult> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.country) q.set('country', params.country);
  if (params.partner) q.set('partner', params.partner);
  if (params.stav) q.set('stav', params.stav);
  const qs = q.toString();
  return api.get<RondoSegmentListResult>(`/api/v1/rondo/segments${qs ? `?${qs}` : ''}`);
}

export interface RondoPayoffItem {
  id: number;
  rondoUserId: number | null;
  name: string;
  email: string | null;
  country: string;
  currency: string;
  amount: number;
  currencyMismatch: boolean;
  requestedAt: string | null;
  status: 'new' | 'pending' | 'sent' | 'delivered' | 'rejected';
}

export interface RondoPayoffListResult {
  items: RondoPayoffItem[];
  total: number;
}

export function listRondoPayoffs(params: {
  search?: string;
  country?: string;
  status?: string;
  limit?: number;
} = {}): Promise<RondoPayoffListResult> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.country) q.set('country', params.country);
  if (params.status) q.set('status', params.status);
  if (params.limit != null) q.set('limit', String(params.limit));
  const qs = q.toString();
  return api.get<RondoPayoffListResult>(`/api/v1/rondo/payoffs${qs ? `?${qs}` : ''}`);
}

export function listRondoUsers(params: RondoUserListParams = {}): Promise<RondoUserListResult> {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.country) q.set('country', params.country);
  if (params.status) q.set('status', params.status);
  if (params.segment) q.set('segment', params.segment);
  if (params.activeDays != null) q.set('activeDays', String(params.activeDays));
  if (params.inactive90) q.set('inactive90', 'true');
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.offset != null) q.set('offset', String(params.offset));
  const qs = q.toString();
  return api.get<RondoUserListResult>(`/api/v1/rondo/users${qs ? `?${qs}` : ''}`);
}

// Přehled = Soutěže (spinGame) z mirroru p1_dwh_rondo. Hry = ostatní hry z
// mirroru. Tipovačka = live proxy přes Rondo Monitoring API. Tvary 1:1 dle
// produkty.repository.ts / produkty.service.ts.

export interface ProduktyCountryStat {
  country: string;
  plays: number;
  uniqueUsers: number;
}
export interface ProduktyTrendPoint {
  date: string;
  cz: number;
  sk: number;
  ro: number;
  total: number;
}
export interface ProduktyDetailDay {
  date: string;
  cz: number;
  sk: number;
  ro: number;
  total: number;
  uniqueUsers: number;
}
export interface ProduktyPrehledStats {
  latestSnapshot: string | null;
  playsToday: number;
  plays7d: number;
  plays30d: number;
  avgUniqueUsersDay30d: number;
  byCountry: ProduktyCountryStat[];
  trend30d: ProduktyTrendPoint[];
  detail14d: ProduktyDetailDay[];
}

export function getProduktyPrehledStats(): Promise<ProduktyPrehledStats> {
  return api.get<ProduktyPrehledStats>('/api/v1/rondo/produkty/prehled-stats');
}

export interface ProduktyHraItem {
  gameType: string;
  label: string;
  spins7d: number;
  spins30d: number;
  avgUsersDay: number;
  cz: number;
  sk: number;
  ro: number;
}

export function listProduktyHry(): Promise<ProduktyHraItem[]> {
  return api.get<ProduktyHraItem[]>('/api/v1/rondo/produkty/hry');
}

export function getProduktyHraDetail(type: string): Promise<ProduktyPrehledStats> {
  return api.get<ProduktyPrehledStats>(
    `/api/v1/rondo/produkty/hry/detail?type=${encodeURIComponent(type)}`,
  );
}

export interface ProduktyTipovackaItem {
  tipGameId: number;
  market: string;
  title: string;
  status: string;
  matchStartDt: string | null;
  prizeTitle: string | null;
  prizeDescription: string | null;
  betCount: number | null;
  winnersCount: number | null;
}
export interface ProduktyTipovackaResult {
  items: ProduktyTipovackaItem[];
}

export function listProduktyTipovacky(): Promise<ProduktyTipovackaResult> {
  return api.get<ProduktyTipovackaResult>('/api/v1/rondo/produkty/tipovacky');
}

export interface ProduktyTipovackaBet {
  userId: number;
  name: string | null;
  email: string | null;
  country: string | null;
  answer: string | null;
  createdAt: string | null;
  isCorrect: boolean | null;
}
export interface ProduktyTipovackaDetail {
  tipGameId: number;
  market: string | null;
  title: string | null;
  questionText: string | null;
  status: string | null;
  matchStartDt: string | null;
  betCloseDt: string | null;
  drawDt: string | null;
  prizeTitle: string | null;
  prizeDescription: string | null;
  betCount: number | null;
  winnerCount: number | null;
  correctBets: number;
  bets: ProduktyTipovackaBet[];
}

export function getProduktyTipovackaDetail(tipGameId: number): Promise<ProduktyTipovackaDetail> {
  return api.get<ProduktyTipovackaDetail>(
    `/api/v1/rondo/produkty/tipovacky/detail?tipGameId=${encodeURIComponent(String(tipGameId))}`,
  );
}

export type ProduktySoutezKind = 'running' | 'scheduled';

export interface ProduktySoutezItem {
  cacheKey: string;
  kind: ProduktySoutezKind;
  gameId: number | null;
  productScheduleId: number | null;
  productId: number | null;
  name: string | null;
  country: string | null; // "CZ" nebo multi "CZ, RO, SK"
  level: string | null;
  valueCzk: number | null;
  spins: number | null;
  startDt: string | null;
  endDt: string | null;
  status: string | null;
}

export interface ProduktySoutezeResult {
  items: ProduktySoutezItem[];
  total: number;
  sumValue: number;
  months: string[];
}

export function listProduktySouteze(params: {
  kind: ProduktySoutezKind;
  country?: string;
  status?: string;
  month?: string;
  search?: string;
  limit?: number;
}): Promise<ProduktySoutezeResult> {
  const q = new URLSearchParams();
  q.set('kind', params.kind);
  if (params.country) q.set('country', params.country);
  if (params.status) q.set('status', params.status);
  if (params.month) q.set('month', params.month);
  if (params.search) q.set('search', params.search);
  if (params.limit != null) q.set('limit', String(params.limit));
  return api.get<ProduktySoutezeResult>(`/api/v1/rondo/produkty/souteze?${q.toString()}`);
}

export interface ProduktySoutezMeta {
  gameId: number;
  productId: number | null;
  name: string | null;
  country: string | null;
  level: string | null;
  status: string | null;
  valueCzk: number | null;
  spins: number | null;
  startDt: string | null;
  endDt: string | null;
}
export interface ProduktySoutezTopPlayer {
  userId: number;
  name: string | null;
  email: string | null;
  country: string | null;
  spinCount: number;
  winCount: number;
  firstSpin: string | null;
  lastSpin: string | null;
}
export interface ProduktySoutezDetail {
  meta: ProduktySoutezMeta;
  sample: {
    totalSpins: number;
    uniquePlayers: number;
    winSpins: number;
    avgSpinsPerPlayer: number;
  };
  topPlayers: ProduktySoutezTopPlayer[];
}

export function getProduktySoutezDetail(gameId: number): Promise<ProduktySoutezDetail> {
  return api.get<ProduktySoutezDetail>(
    `/api/v1/rondo/produkty/souteze/detail?gameId=${encodeURIComponent(String(gameId))}`,
  );
}

export interface FinanceCashRiskBucket {
  count: number;
  sum: number;
}
export interface FinanceCashCountry {
  country: string;
  currency: string;
  threshold: number;
  totalActive: number;
  withBalance: number;
  belowCount: number;
  belowSum: number;
  aboveCount: number;
  aboveSum: number;
  risk: {
    d0_30: FinanceCashRiskBucket;
    d31_60: FinanceCashRiskBucket;
    d61_90: FinanceCashRiskBucket;
    d90plus: FinanceCashRiskBucket;
  };
}

export function getFinanceCashOverview(): Promise<FinanceCashCountry[]> {
  return api.get<FinanceCashCountry[]>('/api/v1/rondo/finance/cash-overview');
}

export type CashFlowGranularity = 'day' | 'month';
export type CashFlowCountry = 'ALL' | 'CZ' | 'SK' | 'RO';
export interface CashFlowPeriod {
  period: string;
  totalBalance: number;
  outCount: number;
  outSum: number;
  inCount: number;
  inSum: number;
}
export interface CashFlowResult {
  granularity: CashFlowGranularity;
  country: CashFlowCountry;
  currency: string;
  totalCurrentBalance: number;
  periods: CashFlowPeriod[];
}

export function getFinanceCashFlow(params: {
  granularity: CashFlowGranularity;
  country: CashFlowCountry;
}): Promise<CashFlowResult> {
  const q = new URLSearchParams({ granularity: params.granularity, country: params.country });
  return api.get<CashFlowResult>(`/api/v1/rondo/finance/cash-flow?${q.toString()}`);
}

export interface GaCountryMtd {
  revenue: number;
  impressions: number;
  clicks: number;
  prevRevenue: number;
  prevImpressions: number;
  prevClicks: number;
}
export interface GaSummary {
  currency: string;
  mtdPeriod: string;
  countries: Record<'ALL' | 'CZ' | 'SK' | 'RO', GaCountryMtd>;
  lastSyncAt: string | null;
}
export type GaGranularity = 'day' | 'week' | 'month';
export interface GaTimeseriesPeriod {
  label: string;
  key: string;
  revenue: number;
  impressions: number;
  clicks: number;
  ecpm: number;
}
export interface GaTimeseriesResult {
  country: 'ALL' | 'CZ' | 'SK' | 'RO';
  granularity: GaGranularity;
  currency: string;
  periods: GaTimeseriesPeriod[];
}

export function getFinanceGaSummary(): Promise<GaSummary> {
  return api.get<GaSummary>('/api/v1/rondo/finance/ga-summary');
}
export function getFinanceGaTimeseries(params: {
  country: 'ALL' | 'CZ' | 'SK' | 'RO';
  granularity: GaGranularity;
}): Promise<GaTimeseriesResult> {
  const q = new URLSearchParams({ country: params.country, granularity: params.granularity });
  return api.get<GaTimeseriesResult>(`/api/v1/rondo/finance/ga-timeseries?${q.toString()}`);
}

export type AdRevGranularity = 'day' | 'month';
export interface AdRevPartner {
  id: string;
  name: string;
}
export interface AdRevPartnerSlice {
  partner: string;
  partnerName: string;
  impressions: number;
  revenue: number;
  ecpm: number;
  isFinal: boolean;
}
export interface AdRevPeriod {
  key: string;
  label: string;
  impressions: number;
  revenue: number;
  ecpm: number;
  isFinal: boolean;
  byPartner: AdRevPartnerSlice[];
}
export interface AdRevTimeseriesResult {
  partner: string;
  country: 'ALL' | 'CZ' | 'SK' | 'RO';
  granularity: AdRevGranularity;
  currency: string;
  periods: AdRevPeriod[];
}
export interface AdRevCountryMtd {
  revenue: number;
  impressions: number;
  prevRevenue: number;
  prevImpressions: number;
}
export interface AdRevSummary {
  partner: string;
  currency: string;
  mtdPeriod: string;
  countries: Record<'ALL' | 'CZ' | 'SK' | 'RO', AdRevCountryMtd>;
  lastSyncAt: string | null;
  lastDataDate: string | null;
}

export function getFinanceReklamkyPartners(): Promise<AdRevPartner[]> {
  return api.get<AdRevPartner[]>('/api/v1/rondo/finance/reklamky/partners');
}
export function getFinanceReklamkySummary(partner: string): Promise<AdRevSummary> {
  const q = new URLSearchParams({ partner });
  return api.get<AdRevSummary>(`/api/v1/rondo/finance/reklamky/summary?${q.toString()}`);
}
export function getFinanceReklamkyTimeseries(params: {
  partner: string;
  country: 'ALL' | 'CZ' | 'SK' | 'RO';
  granularity: AdRevGranularity;
}): Promise<AdRevTimeseriesResult> {
  const q = new URLSearchParams({
    partner: params.partner,
    country: params.country,
    granularity: params.granularity,
  });
  return api.get<AdRevTimeseriesResult>(`/api/v1/rondo/finance/reklamky/timeseries?${q.toString()}`);
}

export interface TrzbyKlient {
  id: number;
  name: string;
  region: string;
  direction: string;
  ico: string;
  dic: string | null;
  currency: string;
  currencies: string | null;
  active: number;
}
export interface TrzbyPerKlient {
  klient_id: number;
  currency: string;
  direction_calc: string;
  cnt: number;
  net: number;
  total: number;
  paid: number;
  unpaid: number;
}
export interface TrzbyUnpaid {
  currency: string;
  cnt: number;
  unpaid: number;
}
export interface TrzbySync {
  last_sync_at: string | null;
  last_status: string | null;
  invoices_count: number;
  matched_count: number;
}
export interface FioBankSyncStatus {
  ok: boolean;
  lastSyncAt: string | null;
  staleAccounts: { id: number; status: string | null; lastSyncAt: string | null }[];
}
export interface TrzbyPrehledResult {
  year: number;
  month: number;
  klienti: TrzbyKlient[];
  perKlient: TrzbyPerKlient[];
  unpaid: TrzbyUnpaid[];
  sync: TrzbySync | null;
  fioBank: FioBankSyncStatus;
}
export interface TrzbyInvoice {
  id: number;
  klient_id: number;
  klient_name: string;
  region: string;
  direction: string;
  public_id: string | null;
  issue_date: string | null;
  duzp: string | null;
  due_date: string | null;
  paid_date: string | null;
  currency: string;
  total_amount: number;
  vat_total: number;
  net_amount: number;
  paid_amount: number | null;
  unpaid_amount: number | null;
  last_payment_date: string | null;
  cancelled: number | null;
  status: string | null;
  type: string | null;
}
export interface TrzbyInvoicesResult {
  year: number;
  month: number | null;
  klient_id: number | null;
  invoices: TrzbyInvoice[];
  count: number;
}

export function getFinanceTrzbyRealPrehled(params: { year: number; month: number }): Promise<TrzbyPrehledResult> {
  const q = new URLSearchParams({ year: String(params.year), month: String(params.month) });
  return api.get<TrzbyPrehledResult>(`/api/v1/rondo/finance/trzby-real/prehled?${q.toString()}`);
}
export function getFinanceTrzbyRealInvoices(params: {
  year: number;
  month?: number | null;
  klientId?: number | null;
}): Promise<TrzbyInvoicesResult> {
  const q = new URLSearchParams({ year: String(params.year) });
  if (params.month) q.set('month', String(params.month));
  if (params.klientId) q.set('klient_id', String(params.klientId));
  return api.get<TrzbyInvoicesResult>(`/api/v1/rondo/finance/trzby-real/invoices?${q.toString()}`);
}

export type AktivitaPeriod = 'day' | 'week' | 'month' | 'year';
export type AktivitaCountry = 'all' | 'cz' | 'sk' | 'ro';

export interface GaKpiMetrics {
  sessions: number;
  page_views: number;
  active_users: number;
  new_users: number;
  organic_search: number;
  avg_session_duration_sec: number;
  bounce_rate: number;
  engagement_rate: number;
}
export interface GaKpiCompare {
  period: string;
  country: string;
  ranges: unknown;
  current: GaKpiMetrics;
  previous: GaKpiMetrics;
}
export interface GaTrendPoint {
  date: string;
  sessions: number;
  page_views: number;
  active_users: number;
  new_users: number;
  avg_session_duration_sec: number;
  bounce_rate: number;
  organic_search_sessions: number;
}
export interface GaTrend {
  period: string;
  country: string;
  points: GaTrendPoint[];
}
export interface GaPageItem {
  page_path: string;
  page_title: string;
  views: number;
  avg_time_on_page_sec: number;
  bounce_rate: number;
}
export interface GaPages {
  period: string;
  country: string;
  items: GaPageItem[];
}
export interface GaPagesSummaryMetrics {
  views: number;
  page_count: number;
  avg_time_on_page_sec: number;
  bounce_rate: number;
}
export interface GaPagesSummary {
  period: string;
  country: string;
  pathPrefix: string;
  ranges: unknown;
  current: GaPagesSummaryMetrics;
  previous: GaPagesSummaryMetrics;
}
export interface GaPagesTrendPoint {
  date: string;
  views: number;
  avg_time_on_page_sec: number;
}
export interface GaPagesTrend {
  period: string;
  country: string;
  pathPrefix: string;
  points: GaPagesTrendPoint[];
}
export interface GaMeta {
  cache: {
    days_in_cache: number;
    oldest_date: string | null;
    newest_date: string | null;
    last_synced_at: string | null;
  };
}

export interface AhrefsMetrics {
  domain_rating?: number;
  backlinks_total?: number;
  refdomains?: number;
  organic_traffic_estimated?: number;
  organic_keywords?: number;
}
export interface AhrefsSync {
  status: string;
  reason: 'ok' | 'quota' | 'pending' | 'error';
  message: string;
  lastError: string | null;
  lastRunAt: string | null;
  dataThroughDate: string | null;
}
export interface AhrefsOverview {
  country: string;
  metrics: AhrefsMetrics;
  meta: { synced_at: string | null; domain: string | null; date: string | null };
  perCountry?: unknown[];
  sync?: AhrefsSync;
}
export interface AhrefsHistoryPoint {
  date: string;
  domain_rating: number;
  backlinks_total: number;
  refdomains: number;
  organic_traffic_estimated: number;
  organic_keywords: number;
}
export interface AhrefsHistory {
  country: string;
  points: AhrefsHistoryPoint[];
}
export interface AhrefsKeywordItem {
  keyword: string;
  country?: string;
  position: number;
  traffic_estimated: number;
  volume: number;
  url: string;
}
export interface AhrefsKeywords {
  country: string;
  items: AhrefsKeywordItem[];
}

export interface GscKpi {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}
export interface GscOverview {
  period: string;
  country: string;
  kpi: GscKpi;
  meta: { last_synced_at: string | null };
}
export interface GscTrendPoint {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}
export interface GscTrend {
  period: string;
  country: string;
  points: GscTrendPoint[];
}
export interface GscQueryItem {
  query: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}
export interface GscQueries {
  period: string;
  country: string;
  items: GscQueryItem[];
}

const AKT_BASE = '/api/v1/rondo/aktivita';

export function getAktivitaGaKpiCompare(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
}): Promise<GaKpiCompare> {
  const q = new URLSearchParams({ period: params.period, country: params.country });
  return api.get<GaKpiCompare>(`${AKT_BASE}/ga/kpi-compare?${q.toString()}`);
}
export function getAktivitaGaTrend(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
}): Promise<GaTrend> {
  const q = new URLSearchParams({ period: params.period, country: params.country });
  return api.get<GaTrend>(`${AKT_BASE}/ga/trend?${q.toString()}`);
}
export function getAktivitaGaPages(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
  sort?: 'views' | 'time' | 'bounce';
  limit?: number;
  pathPrefix?: string;
}): Promise<GaPages> {
  const q = new URLSearchParams({ period: params.period, country: params.country });
  if (params.sort) q.set('sort', params.sort);
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.pathPrefix) q.set('pathPrefix', params.pathPrefix);
  return api.get<GaPages>(`${AKT_BASE}/ga/pages?${q.toString()}`);
}
export function getAktivitaGaPagesSummary(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
  pathPrefix: string;
}): Promise<GaPagesSummary> {
  const q = new URLSearchParams({
    period: params.period,
    country: params.country,
    pathPrefix: params.pathPrefix,
  });
  return api.get<GaPagesSummary>(`${AKT_BASE}/ga/pages-summary?${q.toString()}`);
}
export function getAktivitaGaPagesTrend(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
  pathPrefix: string;
}): Promise<GaPagesTrend> {
  const q = new URLSearchParams({
    period: params.period,
    country: params.country,
    pathPrefix: params.pathPrefix,
  });
  return api.get<GaPagesTrend>(`${AKT_BASE}/ga/pages-trend?${q.toString()}`);
}
export function getAktivitaGaMeta(): Promise<GaMeta> {
  return api.get<GaMeta>(`${AKT_BASE}/ga/meta`);
}

export function getAktivitaAhrefsOverview(params: {
  country: AktivitaCountry;
}): Promise<AhrefsOverview> {
  const q = new URLSearchParams({ country: params.country });
  return api.get<AhrefsOverview>(`${AKT_BASE}/ahrefs/overview?${q.toString()}`);
}
export function getAktivitaAhrefsHistory(params: {
  country: AktivitaCountry;
  days?: number;
}): Promise<AhrefsHistory> {
  const q = new URLSearchParams({ country: params.country });
  q.set('days', String(params.days ?? 90));
  return api.get<AhrefsHistory>(`${AKT_BASE}/ahrefs/history?${q.toString()}`);
}
export function getAktivitaAhrefsKeywords(params: {
  country: AktivitaCountry;
  limit?: number;
}): Promise<AhrefsKeywords> {
  const q = new URLSearchParams({ country: params.country });
  q.set('limit', String(params.limit ?? 30));
  return api.get<AhrefsKeywords>(`${AKT_BASE}/ahrefs/keywords?${q.toString()}`);
}

export function getAktivitaGscOverview(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
}): Promise<GscOverview> {
  const q = new URLSearchParams({ period: params.period, country: params.country });
  return api.get<GscOverview>(`${AKT_BASE}/gsc/overview?${q.toString()}`);
}
export function getAktivitaGscTrend(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
}): Promise<GscTrend> {
  const q = new URLSearchParams({ period: params.period, country: params.country });
  return api.get<GscTrend>(`${AKT_BASE}/gsc/trend?${q.toString()}`);
}
export function getAktivitaGscQueries(params: {
  period: AktivitaPeriod;
  country: AktivitaCountry;
  limit?: number;
}): Promise<GscQueries> {
  const q = new URLSearchParams({ period: params.period, country: params.country });
  q.set('limit', String(params.limit ?? 50));
  return api.get<GscQueries>(`${AKT_BASE}/gsc/queries?${q.toString()}`);
}

export interface BehavioralSummary {
  totals: { total: number; unique_users: number; first_event: string | null; last_event: string | null };
  topTypes: Array<{ event_type: string; cnt: number; unique_users: number; last_at: string | null }>;
  perDay: Array<{ day: string; event_type: string; cnt: number }>;
  mirror: { status: string; last_success_at: string | null; data_through_date: string | null; rows: number };
}
export function getAktivitaBehavioralSummary(): Promise<BehavioralSummary> {
  return api.get<BehavioralSummary>(`${AKT_BASE}/behavioral/summary`);
}
export interface BehavioralHeatmap { cells: Array<{ dow: number; hour: number; cnt: number }>; }
export function getAktivitaBehavioralHeatmap(eventType?: string): Promise<BehavioralHeatmap> {
  const qs = eventType ? `?event_type=${encodeURIComponent(eventType)}` : '';
  return api.get<BehavioralHeatmap>(`${AKT_BASE}/behavioral/heatmap${qs}`);
}
export interface BehavioralWeatherRow {
  day: string; temp_avg_c: number; precip_mm: number; cloud_pct: number;
  stay_home_idx: number; vacation_intensity: number; events_total: number;
}
export interface BehavioralWeather {
  country: string; days: number; available: boolean; rows: BehavioralWeatherRow[];
  meta: { correlation_stay_home_vs_events: number | null; correlation_vacation_vs_events: number | null };
}
export function getAktivitaBehavioralWeather(country: string, days: number): Promise<BehavioralWeather> {
  return api.get<BehavioralWeather>(`${AKT_BASE}/behavioral/weather-engagement?country=${encodeURIComponent(country)}&days=${days}`);
}

// Rondo Hra s.r.o. master affiliate v Affilmaster. Read-only z p1_dwh_affilmaster.
// MTD cache v p1_space_rondo.rondo_affiliates_mtd_cache.

export interface AffiliateMetrics {
  registrations: number;
  ftd: number;
  depositsCount: number;
  depositsVolume: number;
  commission: number;
  cpa: number;
  rs: number;
  clicks: number;
}
export interface AffiliateLifetimePartner extends AffiliateMetrics {
  campaignId: string;
  campaignName: string;
  country: string | null;
}
export interface AffiliatesLifetimeResult {
  generatedAt: string;
  currency: 'EUR';
  eurRate: number;
  partners: AffiliateLifetimePartner[];
}
export interface AffiliatePeriodPartner {
  campaignId: string;
  campaignName: string;
  country: string | null;
  current: AffiliateMetrics;
  previous: AffiliateMetrics | null;
}
export type AffiliatesPeriod = 'day' | 'week' | 'month' | 'half_year' | 'year';
export interface AffiliatesPeriodResult {
  period: AffiliatesPeriod | 'custom';
  compare: boolean;
  ranges: {
    current: { start: string; end: string };
    previous: { start: string; end: string } | null;
  };
  generatedAt: string;
  currency: 'EUR';
  eurRate: number;
  fromCache: boolean;
  partners: AffiliatePeriodPartner[];
}
export interface CampaignChartPoint {
  date: string;
  clicks: number;
  registrations: number;
  ftdCount: number;
  depositsCount: number;
  depositsVolume: number;
  ngrVolume: number;
  commission: number;
}
export interface CampaignChartResult {
  campaignId: string;
  range: '3' | '6' | '12' | 'all';
  granularity: 'day' | 'month';
  points: CampaignChartPoint[];
  mtd: CampaignChartPoint;
}
export interface AffiliatesSyncMeta {
  lastRunAt: string | null;
  status: string | null;
}

export function getAffiliatesLifetime(): Promise<AffiliatesLifetimeResult> {
  return api.get<AffiliatesLifetimeResult>('/api/v1/rondo/finance/affiliates/lifetime');
}
export function getAffiliatesPeriod(params: {
  period?: AffiliatesPeriod;
  compare?: boolean;
  month?: string | null;
}): Promise<AffiliatesPeriodResult> {
  const q = new URLSearchParams();
  if (params.period) q.set('period', params.period);
  if (params.compare) q.set('compare', '1');
  if (params.month) q.set('month', params.month);
  const qs = q.toString();
  return api.get<AffiliatesPeriodResult>(
    `/api/v1/rondo/finance/affiliates/period${qs ? '?' + qs : ''}`,
  );
}
export function getAffiliatesCampaignChart(params: {
  campaignId: string;
  range?: '3' | '6' | '12' | 'all';
  granularity?: 'day' | 'month';
  month?: string | null;
}): Promise<CampaignChartResult> {
  const q = new URLSearchParams({ campaignId: params.campaignId });
  if (params.range) q.set('range', params.range);
  if (params.granularity) q.set('granularity', params.granularity);
  if (params.month) q.set('month', params.month);
  return api.get<CampaignChartResult>(
    `/api/v1/rondo/finance/affiliates/campaign-chart?${q.toString()}`,
  );
}
export type AffiliatesGranularity = 'day' | 'week' | 'month';
export interface AffiliatesTimeseriesPoint {
  period: string;
  clicks: number;
  registrations: number;
  ftd: number;
  depositsCount: number;
  depositsVolume: number;
  cpa: number;
  rs: number;
  commission: number;
}
export interface AffiliatesTimeseriesResult {
  points: AffiliatesTimeseriesPoint[];
  eurRate: number;
}
export function getAffiliatesTimeseries(granularity: AffiliatesGranularity, campaignId?: string): Promise<AffiliatesTimeseriesResult> {
  const q = new URLSearchParams({ granularity });
  if (campaignId) q.set('campaignId', campaignId);
  return api.get<AffiliatesTimeseriesResult>(
    `/api/v1/rondo/finance/affiliates/timeseries?${q.toString()}`,
  );
}

export function getAffiliatesSyncMeta(): Promise<AffiliatesSyncMeta> {
  return api.get<AffiliatesSyncMeta>('/api/v1/rondo/finance/affiliates/sync-meta');
}
export function runAffiliatesSync(): Promise<{ ok: boolean; partners?: number; error?: string }> {
  return api.post<{ ok: boolean; partners?: number; error?: string }>(
    '/api/v1/rondo/finance/affiliates/sync',
  );
}

export interface GlobalSearchResult {
  kind: string;
  kindLabel: string;
  title: string;
  subtitle: string;
  url: string;
}

// Hledá napříč Rondo Space (uživatelé + úkoly). Backend scopuje výsledky na
// Space callera; min. 2 znaky (kratší dotaz vrací prázdno bez round-tripu).
export function globalSearch(q: string): Promise<{ results: GlobalSearchResult[] }> {
  const term = q.trim();
  if (term.length < 2) return Promise.resolve({ results: [] });
  return api.get<{ results: GlobalSearchResult[] }>(
    `/api/v1/rondo/search?q=${encodeURIComponent(term)}`,
  );
}
